import { themeCssPublicUrl } from '@bridebook/web/lib/wedding-website/motif/r2/css-public-url';

/** No theme yet (empty history / never generated) → the neutral billboard layout primitive, served in-code
 * by the slimmed primitive route. Reads as "no theme yet", never a surprise shipped theme. */
const FALLBACK_PRIMITIVE_ID = 'lp-billboard';
const PRIMITIVE_HREF = `/api/wedding-website/motif/theme/${FALLBACK_PRIMITIVE_ID}`;

/**
 * Base structural defaults the app owns (not a theme concern), emitted as a <style> BEFORE the theme so the
 * theme wins on equal specificity. The principle: content is always rendered; chrome is opt-in.
 * - `.bb-ww__main` is transparent to layout by default (one universal DOM; a sidebar/split theme overrides
 *   `display` to make it a real column).
 * - The chrome hooks (nav / ornament / monogram / backdrop) are hidden by default so themes that don't use them are
 *   unaffected; a theme opts in by setting `display`.
 * - Couple-typed text keeps its line breaks and wraps long unbroken strings like URLs.
 * See layout-motif/docs/bem-contract.md.
 */
export const MOTIF_BASE_CSS =
  '.bb-ww__main{display:contents}' +
  '.bb-ww__nav{display:none}' +
  '.bb-ww__ornament{display:none}' +
  '.bb-ww__monogram{display:none}' +
  '.bb-ww__backdrop{display:none}' +
  '.bb-ww__body,.bb-ww__faq-answer,.bb-ww__schedule-event-description' +
  '{white-space:pre-line;overflow-wrap:break-word;word-break:break-word}';

/**
 * Editor surfaces fetch the theme css inline (bucket-backed hash route, immutable). No hash → the primitive.
 * A content-hash URL is its own cache buster, so the fetched file is cached forever (see use-motif-theme-css).
 */
export const motifEditorCssHref = (cssHash: string | undefined): string =>
  cssHash ? `/api/wedding-website/motif/css/${encodeURIComponent(cssHash)}` : PRIMITIVE_HREF;

/** Guest page <link> — the published CDN file (edge-cached), or the primitive when nothing is published. */
export const motifGuestCssHref = (cssHash: string | undefined): string =>
  cssHash ? themeCssPublicUrl(cssHash) : PRIMITIVE_HREF;

/**
 * The two audiences as a union, so a caller cannot render the layout with no stylesheet: an editor surface
 * must hand over the fetched text, the guest page carries only the published pointer.
 */
export type MotifCssProps =
  | {
      /** Guest render — a real SSR'd <link> at the published CDN URL (edge-cached, cheap at scale). */
      guest: true;
      /** Published theme pointer (website.config.motif.cssHash). */
      guestCssHash: string | undefined;
      inlineCss?: never;
    }
  | {
      guest?: false;
      /** Editor: the client-fetched stylesheet text (use-motif-theme-css), rendered inline. */
      inlineCss: string;
      guestCssHash?: never;
    };

/** Injects the Motif theme stylesheet for whichever audience is rendering. */
export const MotifCssLink = ({ guest, guestCssHash, inlineCss }: MotifCssProps) => {
  if (guest) {
    return (
      <>
        <style>{MOTIF_BASE_CSS}</style>
        <link rel="stylesheet" href={motifGuestCssHref(guestCssHash)} />
      </>
    );
  }
  return (
    <>
      <style>{MOTIF_BASE_CSS}</style>
      <style>{inlineCss}</style>
    </>
  );
};
